/**
 * MobileNav Component
 *
 * Mobile navigation menu that appears on smaller screens.
 * Uses React state for managing the sidebar.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../../utils/tailwind-utils'
import Button from '../ui/button'
import { useAuth } from '@/context/auth-context'

interface MobileNavProps {
  className?: string
}

function MobileNav({ className }: MobileNavProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const { user, isAuthenticated } = useAuth()

  // Close sidebar when clicking outside or pressing escape
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement
      if (isSidebarOpen && !target.closest('[data-mobile-nav]')) {
        setIsSidebarOpen(false)
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (isSidebarOpen && event.key === 'Escape') {
        setIsSidebarOpen(false)
      }
    }

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isSidebarOpen])

  // Prevent scrolling when sidebar is open
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isSidebarOpen])

  const closeSidebar = () => setIsSidebarOpen(false)

  return (
    <>
      {/* Mobile menu button */}
      <button
        type="button"
        className="md:hidden p-2 rounded-md hover:bg-muted"
        onClick={() => setIsSidebarOpen(true)}
        aria-label="Open menu"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-6 h-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
          />
        </svg>
      </button>

      {/* Mobile sidebar */}
      <div
        className={cn(
          'fixed inset-0 z-50 bg-background/80 backdrop-blur-sm transition-opacity md:hidden',
          isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
      >
        <div
          data-mobile-nav
          className={cn(
            'fixed inset-y-0 left-0 z-50 w-3/4 max-w-xs bg-card p-6 shadow-lg transform transition-transform',
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full',
            className
          )}
        >
          {/* Close button */}
          <button
            type="button"
            className="absolute top-4 right-4 p-2 rounded-md hover:bg-muted"
            onClick={closeSidebar}
            aria-label="Close menu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Logo */}
          <Link to="/" className="block mb-8 text-xl font-bold text-primary" onClick={closeSidebar}>
            Boardgames Today
          </Link>

          {/* Navigation Links */}
          <nav className="space-y-6">
            <ul className="space-y-4">
              <li>
                <MobileNavLink to="/" onClick={closeSidebar}>
                  Home
                </MobileNavLink>
              </li>
              <li>
                <MobileNavLink to="/calendar" onClick={closeSidebar}>
                  Calendar
                </MobileNavLink>
              </li>
              <li>
                <MobileNavLink to="/meetups" onClick={closeSidebar}>
                  Meetups
                </MobileNavLink>
              </li>
              <li>
                <MobileNavLink to="/about" onClick={closeSidebar}>
                  About
                </MobileNavLink>
              </li>
            </ul>

            {/* Auth Buttons */}
            <div className="pt-6 border-t border-border space-y-3">
              {isAuthenticated ? (
                <>
                  <Link to="/account" onClick={closeSidebar}>
                    <Button variant="outline" className="w-full justify-center">
                      My Account
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={closeSidebar}>
                    <Button variant="outline" className="w-full justify-center">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/signup" onClick={closeSidebar}>
                    <Button className="w-full justify-center">Sign Up</Button>
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      </div>
    </>
  )
}

interface MobileNavLinkProps {
  to: string
  onClick: () => void
  children: React.ReactNode
}

function MobileNavLink({ to, onClick, children }: MobileNavLinkProps) {
  return (
    <Link
      to={to}
      className="block text-lg font-medium text-foreground hover:text-primary transition-colors"
      onClick={onClick}
    >
      {children}
    </Link>
  )
}

export default MobileNav
